import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, ViewChild, effect } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { mfaCheckmarkIcon, mfaMailIcon, mfaPhoneIcon, mfaTextIcon } from '@ld.consumer-portal/data-uri';
import {
  MfaCardComponent,
  MfaOption,
  MfaVerificationRequest,
  VerificationCodeCardComponent,
  VerificationCodeSubmit,
} from '@ld.consumer-portal/mfa';
import { KeyboardService, PlatformService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export type MfaStep = 'select-method' | 'enter-code' | 'success';

export interface MfaSendCodeRequest {
  contactId: string;
  type: 'email' | 'sms' | 'call';
}

export interface MfaVerifyCodeRequest {
  code: string;
  rememberDevice: boolean;
}

@Component({
  selector: 'ld-link-loan-mfa-modal',
  imports: [
    UpperCasePipe,
    BaseModalComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    RouterLink,
    MfaCardComponent,
    VerificationCodeCardComponent,
    TranslocoDirective,
  ],
  templateUrl: './link-loan-mfa-modal.component.html',
  styleUrl: './link-loan-mfa-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkLoanMfaModalComponent {
  readonly checkmarkIcon = mfaCheckmarkIcon;

  @ViewChild('verificationCodeCard') verificationCodeCard?: VerificationCodeCardComponent;
  @ViewChild('mfaCard') mfaCard?: MfaCardComponent;

  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly keyboardService = inject(KeyboardService);

  protected readonly isBottomSheet = this.platformService.isNativeMobile;
  protected readonly keyboardHeight = this.keyboardService.keyboardOverlayHeight;

  protected readonly mailIcon = mfaMailIcon;
  protected readonly textIcon = mfaTextIcon;
  protected readonly phoneIcon = mfaPhoneIcon;

  protected readonly selectedBsEmail = signal<MfaOption | null>(null);
  protected readonly selectedBsPhone = signal<MfaOption | null>(null);
  protected readonly selectedBsCallPhone = signal<MfaOption | null>(null);

  protected readonly initialBsEmail = computed(() => this.emailOptions()[0] ?? null);
  protected readonly initialBsPhone = computed(() => this.textOptions()[0] ?? null);
  protected readonly initialBsCallPhone = computed(() => this.callOptions()[0] ?? null);

  private readonly resetMfaCardLoadingOnError = effect(() => {
    const error = this.errorMessage();
    if (error) {
      this.mfaCard?.resetLoading();
    }
  });

  private readonly clearBsSelectionOnLoadingDone = effect(() => {
    if (!this.isLoading()) {
      this.selectedBsEmail.set(null);
      this.selectedBsPhone.set(null);
      this.selectedBsCallPhone.set(null);
    }
  });

  isOpen = input<boolean>(false);

  embedded = input<boolean>(false);

  emailOptions = input<MfaOption[]>([]);

  textOptions = input<MfaOption[]>([]);

  callOptions = input<MfaOption[]>([]);

  isFirstTimeLinking = input<boolean>(false);

  protected readonly isEmailOnly = computed(() => {
    return this.emailOptions().length > 0 && this.textOptions().length === 0 && this.callOptions().length === 0;
  });

  isLoading = input<boolean>(false);

  errorMessage = input<string | null>(null);

  maskedContact = input<string>('');

  sendCode = output<MfaSendCodeRequest>();

  verifyCode = output<MfaVerifyCodeRequest>();

  resendCode = output<void>();

  cancel = output<void>();

  confirmSuccess = output<void>();

  protected readonly currentStep = signal<MfaStep>('select-method');

  protected readonly selectedVerificationType = signal<'email' | 'text' | 'call'>('email');

  protected readonly isVisible = signal(true);

  protected getMfaDescription(): string | null {
    if (this.isEmailOnly()) {
      return this.transloco.translate('mfaLinkLoanEmailOnlyDescription');
    }
    return null;
  }

  protected onEmailVerification(request: MfaVerificationRequest): void {
    this.selectedVerificationType.set('email');
    this.sendCode.emit({
      contactId: request.value,
      type: 'email',
    });
  }

  protected onTextVerification(request: MfaVerificationRequest): void {
    this.selectedVerificationType.set('text');
    this.sendCode.emit({
      contactId: request.value,
      type: 'sms',
    });
  }

  protected onCallVerification(request: MfaVerificationRequest): void {
    this.selectedVerificationType.set('call');
    this.sendCode.emit({
      contactId: request.value,
      type: 'call',
    });
  }

  protected onMfaCancel(): void {
    this.cancel.emit();
  }

  protected onVerify(event: VerificationCodeSubmit): void {
    this.verifyCode.emit({
      code: event.code,
      rememberDevice: event.rememberDevice,
    });
  }

  protected onResend(): void {
    this.resendCode.emit();
  }

  protected onVerificationCancel(): void {
    this.currentStep.set('select-method');
  }

  protected onSuccessConfirm(): void {
    this.confirmSuccess.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isLoading()) {
      this.cancel.emit();
    }
  }

  goBack(): void {
    if (this.currentStep() === 'enter-code') {
      this.currentStep.set('select-method');
    } else {
      this.cancel.emit();
    }
  }

  showCodeEntry(): void {
    this.currentStep.set('enter-code');
  }

  showSuccess(): void {
    this.currentStep.set('success');
  }

  reset(): void {
    this.currentStep.set('select-method');
    this.selectedVerificationType.set('email');
    this.isVisible.set(true);
    this.selectedBsEmail.set(null);
    this.selectedBsPhone.set(null);
    this.selectedBsCallPhone.set(null);
  }

  setVerificationError(message: string): void {
    this.verificationCodeCard?.setError(message);
  }

  setResendSuccess(message: string): void {
    this.verificationCodeCard?.setSuccess(message);
  }

  protected getVerificationTypeDisplay(): 'email' | 'text' | 'call' {
    return this.selectedVerificationType();
  }

  protected onBsSelectEmail(): void {
    const option = this.selectedBsEmail() ?? this.initialBsEmail();
    if (option) {
      this.onEmailVerification({ method: 'email', value: option.value });
    }
  }

  protected onBsSelectText(): void {
    const option = this.selectedBsPhone() ?? this.initialBsPhone();
    if (option) {
      this.onTextVerification({ method: 'text', value: option.value });
    }
  }

  protected onBsSelectCall(): void {
    const option = this.selectedBsCallPhone() ?? this.initialBsCallPhone();
    if (option) {
      this.onCallVerification({ method: 'call', value: option.value });
    }
  }
}
